// src/pages/CreateAuthorTitlePage.jsx
// Independent Author Title Upload - Title-first flow (mirrors Managed Author pattern)
// Authors upload their own work - no Publisher attestation required
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import CreatorTopMenuBar from '../components/CreatorTopMenuBar';
import OutlineViewerModalV3 from '../components/OutlineViewerModalV3';
import TTSDecisionModal from '../components/TTSDecisionModal';
// P0 DIRECTIVE: TTSProgressModal is DEPRECATED and FORBIDDEN
// TTS is background-only, progress shown via polling surfaces
import { Upload, ArrowLeft, FileText, Loader2, Image, X, CheckCircle, BookOpen } from 'lucide-react';
import UniversalFooter from '../components/UniversalFooter';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useNotification } from '@/contexts/NotificationContext';
import axios from 'axios';

// Import genre/theme constants
import { BOOK_TYPE_OPTIONS, GENRE_OPTIONS, getGenresForBookType, getThemesForGenre } from '../constants/genreThemes';
import { BACKEND_URL as backendUrl } from '@/config/api';

const CreateAuthorTitlePage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const coverInputRef = useRef(null);
  
  const { notifyError, notifySuccess } = useNotification();
  const [authorProfile, setAuthorProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    title_name: '',
    subtitle: '',
    isbn: '',
    book_type: '',
    genre: '',
    theme: '',
    purchase_url: '',
    is_promotional: false,
    description: ''
  });
  
  // File states
  const [manuscriptFile, setManuscriptFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  
  // Upload progress modal
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ stage: '', percent: 0 });
  
  // Post-upload flow states
  const [showOutlineModal, setShowOutlineModal] = useState(false);
  const [showTTSModal, setShowTTSModal] = useState(false);
  const [showTTSProgress, setShowTTSProgress] = useState(false);
  const [ttsProgress, setTTSProgress] = useState({ completed: 0, total: 0, isComplete: false });
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  
  // Created title/manuscript tracking
  const [createdTitleId, setCreatedTitleId] = useState(null);
  const [createdManuscriptId, setCreatedManuscriptId] = useState(null);
  const [pendingManuscriptData, setPendingManuscriptData] = useState(null);
  
  const [errors, setErrors] = useState({});
  // backendUrl imported from centralized config as BACKEND_URL

  useEffect(() => {
    fetchAuthorProfile();
  }, []);

  const fetchAuthorProfile = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${backendUrl}/api/author/author-profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.exists === false) {
          // No author profile - redirect to create one
          notifyError('Please create your author profile first');
          navigate('/author');
          return;
        }
        setAuthorProfile(data);
      } else {
        notifyError('Failed to load author profile');
        navigate('/author');
      }
    } catch (error) {
      console.error('Error fetching author profile:', error);
      notifyError('Failed to load author profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type - EPUB only for now
    if (!file.name.toLowerCase().endsWith('.epub')) {
      notifyError('Please select an EPUB file');
      return;
    }
    
    // Validate file size (20MB limit)
    if (file.size > 20 * 1024 * 1024) {
      notifyError('File size exceeds 20MB limit');
      return;
    }
    
    setManuscriptFile(file);
  };

  const handleCoverSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      notifyError('Please select a valid image file (JPG, PNG, or WebP)');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      notifyError('Cover image exceeds 5MB limit');
      return;
    }
    
    setCoverFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setCoverPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title_name.trim()) {
      newErrors.title_name = 'Title is required';
    }
    
    if (!manuscriptFile) {
      newErrors.manuscript = 'Manuscript file is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      notifyError('Please fix the errors above');
      return;
    }
    
    setUploading(true);
    setShowProgressModal(true);
    setUploadProgress({ stage: 'Creating title record...', percent: 10 });
    
    try {
      const token = localStorage.getItem('accessToken');
      
      // STEP 1: Create title record
      const titlePayload = {
        title_name: formData.title_name,
        subtitle: formData.subtitle || null,
        description: formData.description || null,
        isbn: formData.isbn || null,
        book_type: formData.book_type || null,
        genre: formData.genre || null,
        theme: formData.theme || null,
        purchase_url: formData.purchase_url || null,
        is_promotional: formData.is_promotional
      };
      
      const titleResponse = await fetch(`${backendUrl}/api/author/titles`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(titlePayload)
      });
      
      if (!titleResponse.ok) {
        const error = await titleResponse.json();
        throw new Error(error.detail || 'Failed to create title record');
      }
      
      const titleData = await titleResponse.json();
      const titleId = titleData.id;
      setCreatedTitleId(titleId);
      
      // STEP 2: Upload cover image if provided
      if (coverFile) {
        setUploadProgress({ stage: 'Uploading cover image...', percent: 20 });
        
        try {
          const coverFormData = new FormData();
          coverFormData.append('cover', coverFile);
          
          const coverResponse = await fetch(`${backendUrl}/api/author/titles/${titleId}/cover`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: coverFormData
          });
          
          if (!coverResponse.ok) {
            console.warn('Cover upload warning - continuing with manuscript upload');
          }
        } catch (coverError) {
          console.warn('Cover upload error (non-fatal):', coverError.message);
        }
      }
      
      setUploadProgress({ stage: 'Uploading manuscript...', percent: 30 });
      
      // STEP 3: Upload manuscript and trigger ingestion
      const manuscriptFormData = new FormData();
      manuscriptFormData.append('file', manuscriptFile);
      manuscriptFormData.append('upload_format', 'epub');
      
      const uploadData = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              reject(new Error('Invalid response from server'));
            }
          } else {
            try {
              const error = JSON.parse(xhr.responseText);
              reject(new Error(error.detail || 'Failed to upload manuscript'));
            } catch (e) {
              reject(new Error('Failed to upload manuscript'));
            }
          }
        };
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.open('POST', `${backendUrl}/api/author/titles/${titleId}/upload-manuscript`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(manuscriptFormData);
      });
      
      setUploadProgress({ stage: 'Processing manuscript...', percent: 60 });
      
      const manuscriptId = uploadData.manuscript_id;
      setCreatedManuscriptId(manuscriptId);
      
      // Background processing - poll until complete with exponential backoff
      if (uploadData.status === 'processing' || uploadData.next_step === 'poll_status') {
        setUploadProgress({ stage: 'Processing in background (this may take a few minutes for large files)...', percent: 65 });
        
        let attempts = 0;
        const maxAttempts = 90; // ~5 minutes with backoff
        let delay = 1000;
        
        while (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, delay));
          attempts++;
          
          // Exponential backoff
          if (attempts > 10 && delay < 2000) delay = 2000;
          if (attempts > 20 && delay < 3000) delay = 3000;
          if (attempts > 30 && delay < 5000) delay = 5000;
          
          try {
            const statusResponse = await fetch(`${backendUrl}/api/manuscripts/${manuscriptId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (statusResponse.ok) {
              const statusData = await statusResponse.json();
              const currentStatus = statusData.status || statusData.processing_stage;
              
              if (currentStatus === 'ingesting' || statusData.processing_stage === 'ephemeral_intelligence') {
                setUploadProgress({ stage: 'Generating intelligence...', percent: 70 });
              } else if (statusData.processing_stage === 'nfd_pipeline') {
                setUploadProgress({ stage: 'Extracting content structure...', percent: 80 });
              } else if (statusData.processing_stage === 'segments_stored') {
                setUploadProgress({ stage: 'Storing segments...', percent: 85 });
              } else if (currentStatus === 'processed' || statusData.processing_stage === 'complete' || statusData.processing_stage === 'completed') {
                setUploadProgress({ stage: 'Content extraction complete!', percent: 90 });
                setPendingManuscriptData(statusData);
                break;
              } else if (currentStatus === 'failed') {
                throw new Error(statusData.error_message || 'Processing failed');
              }
            }
          } catch (pollError) {
            if (pollError.message.includes('Processing failed')) throw pollError;
            console.warn('[Upload] Poll error:', pollError.message);
          }
        }
        
        if (attempts >= maxAttempts) {
          notifySuccess('Processing continues in background. Redirecting to Titles page...');
          setTimeout(() => {
            navigate('/author/titles');
          }, 2000);
          return;
        }
      } else {
        setUploadProgress({ stage: 'Content extraction complete!', percent: 90 });
        
        const msResponse = await fetch(`${backendUrl}/api/manuscripts/${manuscriptId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (msResponse.ok) {
          const msData = await msResponse.json();
          setPendingManuscriptData(msData);
        }
      }
      
      setUploadProgress({ stage: 'Ready for outline review!', percent: 100 });
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Close progress modal and show outline editor
      setShowProgressModal(false);
      setUploading(false);
      setShowOutlineModal(true);
      
      console.log('[SUCCESS] ' + 'Content extracted! Please review the outline.');
      
    } catch (error) {
      console.error('Error in upload flow:', error);
      notifyError(error.message || 'Upload failed');
      setShowProgressModal(false);
      setUploading(false);
    }
  };

  // Handle outline save - proceed to TTS
  const handleOutlineSave = async (data) => {
    console.log('[Author Upload] Outline saved, proceeding to TTS');
    setShowOutlineModal(false);
    
    // CRITICAL: Refresh manuscript data to get updated segment counts after edits
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${backendUrl}/api/v3/manuscripts/${createdManuscriptId}/outline`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const outlineData = await response.json();
        console.log('[Author Upload] Refreshed outline data:', outlineData);
        
        // Update pendingManuscriptData with fresh segment/word counts
        setPendingManuscriptData(prev => ({
          ...prev,
          segment_count: outlineData.segment_count,
          word_count: outlineData.total_word_count,
          character_count: outlineData.total_character_count,
          chapter_count: outlineData.segment_count
        }));
      }
    } catch (error) {
      console.error('[Author Upload] Failed to refresh outline data:', error);
    }
    
    setShowTTSModal(true);
  };

  // State for TTS started confirmation modal
  const [showTTSStartedModal, setShowTTSStartedModal] = useState(false);
  const [ttsStarting, setTtsStarting] = useState(false); // Prevent double-click

  // Handle TTS start - NEW FLOW: Start → Confirm → Redirect to /author (Author Console)
  // IDEMPOTENT: Button disabled during request to prevent duplicate jobs
  const handleStartTTS = async (mode = 'full') => {
    // Guard against double-click
    if (ttsStarting) {
      console.log('[Author Upload] TTS start already in progress, ignoring duplicate click');
      return;
    }
    
    console.log('[Author Upload] Starting TTS generation in mode:', mode);
    setTtsStarting(true);
    setShowTTSModal(false);
    
    try {
      // P0 FINAL HOLE: TTS is auto-triggered by ingestion completion
      // User does NOT start TTS manually - system handles it automatically
      console.log('[CreateAuthorTitlePage] TTS will be auto-triggered after ingestion completes');
      
      // Show confirmation modal, then redirect to author console
      setShowTTSStartedModal(true);
      
    } catch (error) {
      console.error('[Author Upload] Upload failed:', error);
      notifyError('Failed to upload manuscript');
      setTtsStarting(false);
    }
  };
  
  // Handle TTS started confirmation - redirect to author console
  const handleTTSStartedConfirm = () => {
    setShowTTSStartedModal(false);
    // Redirect to author console where they can see their titles
    navigate('/author');
  };

  // P0 FINAL HOLE: TTS polling REMOVED - Status comes from page load only
  // pollTTSProgress DELETED - No polling allowed

  // Handle completion
  const handleCompletionClose = () => {
    setShowCompletionModal(false);
    navigate('/author');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-50">
        <CreatorTopMenuBar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      </div>
    );
  }

  const authorDisplayName = authorProfile?.pen_name || 
    `${authorProfile?.first_name || ''} ${authorProfile?.last_name || ''}`.trim() || 
    'Author';

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <CreatorTopMenuBar />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/author" 
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Author Console
          </Link>
          
          <h1 className="text-3xl font-bold text-white mb-2">Create New Title</h1>
          <p className="text-slate-400">
            Upload your manuscript as <span className="text-purple-400 font-medium">{authorDisplayName}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Title Information */}
          <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-800">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-purple-400" />
              Title Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Label htmlFor="title_name" className="text-slate-300">
                  Title <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="title_name"
                  value={formData.title_name}
                  onChange={(e) => handleInputChange('title_name', e.target.value)}
                  placeholder="Enter your book title"
                  className={`bg-slate-800 border-slate-700 text-white ${errors.title_name ? 'border-red-500' : ''}`}
                />
                {errors.title_name && (
                  <p className="text-red-400 text-sm mt-1">{errors.title_name}</p>
                )}
              </div>
              
              <div className="md:col-span-2">
                <Label htmlFor="subtitle" className="text-slate-300">Subtitle</Label>
                <Input
                  id="subtitle"
                  value={formData.subtitle}
                  onChange={(e) => handleInputChange('subtitle', e.target.value)}
                  placeholder="Optional subtitle"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              
              <div>
                <Label htmlFor="isbn" className="text-slate-300">ISBN</Label>
                <Input
                  id="isbn"
                  value={formData.isbn}
                  onChange={(e) => handleInputChange('isbn', e.target.value)}
                  placeholder="Optional - 10 or 13 digits"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              
              <div>
                <Label htmlFor="purchase_url" className="text-slate-300">Purchase URL</Label>
                <Input
                  id="purchase_url"
                  type="url"
                  value={formData.purchase_url}
                  onChange={(e) => handleInputChange('purchase_url', e.target.value)}
                  placeholder="https://..."
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              
              <div>
                <Label htmlFor="book_type" className="text-slate-300">Book Type</Label>
                <select
                  id="book_type"
                  value={formData.book_type}
                  onChange={(e) => {
                    handleInputChange('book_type', e.target.value);
                    handleInputChange('genre', '');
                    handleInputChange('theme', '');
                  }}
                  className="w-full h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-white"
                >
                  <option value="">Select type...</option>
                  {BOOK_TYPE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label htmlFor="genre" className="text-slate-300">Genre</Label>
                <select
                  id="genre"
                  value={formData.genre}
                  onChange={(e) => {
                    handleInputChange('genre', e.target.value);
                    handleInputChange('theme', '');
                  }}
                  disabled={!formData.book_type}
                  className="w-full h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-white disabled:opacity-50"
                >
                  <option value="">Select genre...</option>
                  {getGenresForBookType(formData.book_type).map(g => (
                    <option key={g.value} value={g.value}>{g.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="md:col-span-2">
                <Label htmlFor="theme" className="text-slate-300">Theme</Label>
                <select
                  id="theme"
                  value={formData.theme}
                  onChange={(e) => handleInputChange('theme', e.target.value)}
                  disabled={!formData.genre}
                  className="w-full h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-white disabled:opacity-50"
                >
                  <option value="">Select theme...</option>
                  {getThemesForGenre(formData.genre).map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="md:col-span-2">
                <Label htmlFor="description" className="text-slate-300">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Brief description of your book..."
                  rows={4}
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_promotional}
                    onChange={(e) => handleInputChange('is_promotional', e.target.checked)}
                    className="w-5 h-5 rounded border-slate-600 bg-slate-800 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-slate-300">
                    This is a promotional/free book (no purchase required)
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Cover Image */}
          <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-800">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Image className="w-5 h-5 text-cyan-400" />
              Cover Image
            </h2>
            
            <div className="flex items-start gap-6">
              {coverPreview ? (
                <div className="relative">
                  <img 
                    src={coverPreview} 
                    alt="Cover preview" 
                    className="w-32 h-48 object-cover rounded-lg border border-slate-700"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setCoverFile(null);
                      setCoverPreview(null);
                    }}
                    className="absolute -top-2 -right-2 p-1 bg-red-600 rounded-full text-white hover:bg-red-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="w-32 h-48 bg-slate-800 rounded-lg border-2 border-dashed border-slate-700 flex items-center justify-center">
                  <Image className="w-8 h-8 text-slate-600" />
                </div>
              )}
              
              <div className="flex-1">
                <input
                  ref={coverInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleCoverSelect}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => coverInputRef.current?.click()}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {coverPreview ? 'Change Cover' : 'Upload Cover'}
                </Button>
                <p className="text-sm text-slate-500 mt-2">
                  JPG, PNG, or WebP. Max 5MB. Recommended: 1600x2400px
                </p>
              </div>
            </div>
          </div>

          {/* Manuscript Upload */}
          <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-800">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <FileText className="w-5 h-5 text-green-400" />
              Manuscript File <span className="text-red-400">*</span>
            </h2>
            
            <div className="space-y-4">
              <input
                ref={fileInputRef}
                type="file"
                accept=".epub"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {manuscriptFile ? (
                <div className="flex items-center gap-4 p-4 bg-slate-800 rounded-lg">
                  <FileText className="w-8 h-8 text-green-400" />
                  <div className="flex-1">
                    <p className="text-white font-medium">{manuscriptFile.name}</p>
                    <p className="text-sm text-slate-400">
                      {(manuscriptFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setManuscriptFile(null)}
                    className="p-2 text-slate-400 hover:text-red-400"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className={`w-full p-8 border-2 border-dashed rounded-lg text-center transition-colors ${
                    errors.manuscript 
                      ? 'border-red-500 bg-red-950/20' 
                      : 'border-slate-700 hover:border-purple-500 bg-slate-800/50'
                  }`}
                >
                  <Upload className="w-10 h-10 mx-auto mb-3 text-slate-500" />
                  <p className="text-white font-medium mb-1">Click to upload EPUB</p>
                  <p className="text-sm text-slate-500">Maximum file size: 20MB</p>
                </button>
              )}
              
              {errors.manuscript && (
                <p className="text-red-400 text-sm">{errors.manuscript}</p>
              )}
              
              {/* Manuscript Upload Policy Helper Text */}
              <p className="text-xs text-slate-500 mt-2 bg-slate-800/30 rounded-lg px-3 py-2 border border-slate-700/50">
                Your first manuscript is free to publish. Additional manuscripts require a $9.99 activation fee, charged when you activate the title.
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/author')}
              className="border-slate-700 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={uploading}
              className="bg-purple-600 hover:bg-purple-700 text-white px-8"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Create Title & Upload
                </>
              )}
            </Button>
          </div>
        </form>
      </main>

      {/* Progress Modal */}
      <Dialog open={showProgressModal} onOpenChange={() => {}}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Uploading Your Book</DialogTitle>
            <DialogDescription className="text-slate-400">
              Please wait while we process your manuscript...
            </DialogDescription>
          </DialogHeader>
          <div className="py-6">
            <div className="flex items-center gap-4 mb-4">
              <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
              <span className="text-white">{uploadProgress.stage}</span>
            </div>
            <div className="w-full bg-slate-800 rounded-full h-2">
              <div 
                className="bg-purple-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${uploadProgress.percent}%` }}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Outline Editor Modal */}
      <OutlineViewerModalV3
        isOpen={showOutlineModal}
        onClose={() => {
          setShowOutlineModal(false);
          setShowCompletionModal(true);
        }}
        onSaveAndContinue={handleOutlineSave}
        manuscriptId={createdManuscriptId}
      />

      {/* TTS Decision Modal */}
      <TTSDecisionModal
        isOpen={showTTSModal}
        manuscriptData={pendingManuscriptData}
        onYes={(mode) => handleStartTTS(mode)}
        onNo={() => {
          setShowTTSModal(false);
          setShowCompletionModal(true);
        }}
      />

      {/* TTS Started Confirmation Modal - NEW: Replaces TTSProgressModal as default */}
      <Dialog open={showTTSStartedModal} onOpenChange={() => {}}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-green-500" />
              Audio Generation Started
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-300 mb-4">
              Audio generation is running in the background. You will now be redirected to your <strong>Author Console</strong> where progress is tracked.
            </p>
            <p className="text-slate-400 text-sm">
              This process typically takes 30-60 minutes to complete. You can safely close your browser and return later.
            </p>
          </div>
          <Button
            onClick={handleTTSStartedConfirm}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            OK
          </Button>
        </DialogContent>
      </Dialog>

      {/* P0 DIRECTIVE: TTSProgressModal is FORBIDDEN and has been REMOVED */}
      {/* TTS progress is shown via polling on /creator/home page */}

      {/* Completion Modal */}
      <Dialog open={showCompletionModal} onOpenChange={handleCompletionClose}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-green-500" />
              Title Created Successfully!
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-300 mb-4">
              Your title <span className="text-white font-semibold">{formData.title_name}</span> has been created and is now available in your library.
            </p>
            <Button
              onClick={handleCompletionClose}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              Return to Author Console
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <UniversalFooter />
    </div>
  );
};

export default CreateAuthorTitlePage;
